import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { ApiIntegration, ApiIntegrationsResponse } from '../interfaces/ApiIntegrations';

const RESOURCE_URL = '/apiIntegrations';

export const useApiIntegrations = (page: number = 1, search?: string) => {
  return useQuery<ApiIntegrationsResponse, Error>(['apiIntegrations', page, search], async () => {
    const params = new URLSearchParams({ page: page.toString() });
    if (search) params.append('search', search);

    const { data } = await api.get(`${RESOURCE_URL}?${params.toString()}`);
    return data;
  });
};

export const useApiIntegration = (id: string) => {
  return useQuery<{ data: ApiIntegration }, Error>(['apiIntegration', id], async () => {
    const { data } = await api.get(`${RESOURCE_URL}/${id}`);
    return data;
  }, {
    enabled: !!id,
  });
};

export const useCreateApiIntegration = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: ApiIntegration) => {
      const { data } = await api.post(RESOURCE_URL, payload);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['apiIntegrations'] });
      },
    }
  );
};

export const useUpdateApiIntegration = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, payload }: { id: string; payload: Partial<ApiIntegration> }) => {
      const { data } = await api.put(`${RESOURCE_URL}/${id}`, payload);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['apiIntegrations'] });
      },
    }
  );
};

export const useDeleteApiIntegration = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>(
    async (id: string) => {
      await api.delete(`${RESOURCE_URL}/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['apiIntegrations'] });
      },
    }
  );
};

export const useSyncApiIntegration = () => {
  return useMutation<any, Error, { id: string; startDate: string; endDate: string }>(
    async ({ id, startDate, endDate }) => {
      const { data } = await api.post(`${RESOURCE_URL}/${id}/sync`, { startDate, endDate });
      return data;
    }
  );
};

export default {
  useApiIntegrations,
  useApiIntegration,
  useCreateApiIntegration,
  useUpdateApiIntegration,
  useDeleteApiIntegration,
};
